import type { ReactNode } from "react";

import { euro } from "../../Rechts/opmaak";
import { datumKort } from "../opmaak";
import type { KaartCoulance, KaartCoulanceRegel } from "../types";
import { NogNiet } from "./NogNiet";

/**
 * Coulance, tegoed en kortingscodes (ontwerp 31 punt 8, sessie 73 blok 4).
 * Bron: het register van sessie 55 (`klant_coulance.overzicht`), met de
 * samengevoegde accounts erbij. Kan het register niet gelezen worden
 * (`null`), dan "nog niet bijgehouden". Staat er niets in, dan "geen
 * vastgelegd": oude gevallen komen pas in het register na het vulscript,
 * dus een 0 zou een leugen zijn.
 */
const COULANCE_SOORTEN = new Set(["coulance", "terugbetaling", "nazending"]);
/** Zelfde venster als het register (183 dagen). */
const ZES_MAANDEN_MS = 183 * 24 * 60 * 60 * 1000;

function nieuwste(
  regels: KaartCoulanceRegel[],
  past: (r: KaartCoulanceRegel) => boolean,
): KaartCoulanceRegel | undefined {
  return regels.find(past);
}

function Tegel({ titel, waarde, onder }: { titel: string; waarde: ReactNode; onder?: string }) {
  return (
    <div className="kk-cijfer">
      <span className="l">{titel}</span>
      <span className="w">{waarde}</span>
      {onder ? <span className="o">{onder}</span> : null}
    </div>
  );
}

const GEEN = <span className="kk-nognniet">geen vastgelegd</span>;

export function Coulance({ coulance }: { coulance?: KaartCoulance | null }) {
  const label = "Coulance, tegoed en kortingscodes";
  if (!coulance) {
    return (
      <div className="kk-cijfers compact" aria-label={label}>
        {["Coulance", "Tegoed", "Kortingscodes"].map((titel) => (
          <Tegel key={titel} titel={titel} waarde={<NogNiet />} />
        ))}
      </div>
    );
  }
  const { regels, per_soort } = coulance;

  // Coulance: aantal in zes maanden (alleen coulance, terugbetaling en
  // nazending; `laatste_6_maanden` telt ook codes en tegoed), laatste voorval.
  const grens = Date.now() - ZES_MAANDEN_MS;
  const recent = regels.filter(
    (r) =>
      r.status === "uitgevoerd" && COULANCE_SOORTEN.has(r.soort) && Date.parse(r.op) >= grens,
  ).length;
  const laatsteCoulance = nieuwste(
    regels,
    (r) => r.status === "uitgevoerd" && COULANCE_SOORTEN.has(r.soort),
  );
  const coulanceOnder = laatsteCoulance
    ? [
        `laatst ${datumKort(laatsteCoulance.op)}`,
        laatsteCoulance.bedrag_cents != null ? euro(laatsteCoulance.bedrag_cents) : null,
      ]
        .filter(Boolean)
        .join(", ")
    : undefined;

  // Tegoed: uitgevoerd bedrag, open (voorgesteld) tegoed erbij.
  const tegoed = per_soort.tegoed;
  const openTegoed = regels.filter((r) => r.soort === "tegoed" && r.status === "voorgesteld");

  // Kortingscodes: gegeven, laatste code, open.
  const codes = per_soort.kortingscode;
  const laatsteCode = nieuwste(regels, (r) => r.soort === "kortingscode" && r.status === "uitgevoerd");
  const openCodes = regels.filter((r) => r.soort === "kortingscode" && r.status === "voorgesteld");

  return (
    <div className="kk-cijfers compact" aria-label={label}>
      <Tegel
        titel="Coulance"
        waarde={recent > 0 ? `${recent}x in 6 mnd` : GEEN}
        onder={coulanceOnder}
      />
      <Tegel
        titel="Tegoed"
        waarde={tegoed && tegoed.aantal > 0 ? euro(tegoed.bedrag_cents) : GEEN}
        onder={openTegoed.length > 0 ? `${openTegoed.length} open` : undefined}
      />
      <Tegel
        titel="Kortingscodes"
        waarde={codes && codes.aantal > 0 ? `${codes.aantal} gegeven` : GEEN}
        onder={
          [
            laatsteCode?.code ? `laatst ${laatsteCode.code}, ${datumKort(laatsteCode.op)}` : null,
            openCodes.length > 0 ? `${openCodes.length} open` : null,
          ]
            .filter(Boolean)
            .join(" · ") || undefined
        }
      />
    </div>
  );
}
